import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  startAfter,
  DocumentSnapshot,
  getCountFromServer
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Post, AIAnalysis, PostStatistics, ThreadNote } from '../types/index';
import { ThemeService } from './themeService';

export class PostService {
  // Flutter版と同じ投稿作成メソッド
  static async createPost(postData: {
    anonymousId: string;
    type: 'voice' | 'thanks' | 'theme';
    category: string;
    content: string;
    tags: string[];
    urgency: 'low' | 'medium' | 'high';
    themeId?: string;
    aiAnalysis: AIAnalysis;
    statistics: PostStatistics;
    attachments?: any[];
  }): Promise<string> {
    try {
      // undefinedの値を除外してFirestoreに送信
      const data: any = {
        anonymousId: postData.anonymousId,
        type: postData.type,
        category: postData.category,
        content: postData.content,
        tags: postData.tags,
        urgency: postData.urgency,
        aiAnalysis: postData.aiAnalysis,
        statistics: postData.statistics,
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      
      // themeIdが存在する場合のみ追加
      if (postData.themeId) {
        data.themeId = postData.themeId;
      }
      
      // attachmentsが存在する場合のみ追加
      if (postData.attachments) {
        data.attachments = postData.attachments;
      }
      
      const docRef = await addDoc(collection(db, 'posts'), data);
      
      // テーマ投稿の場合はテーマの投稿数を更新
      if (postData.themeId) {
        try {
          await ThemeService.incrementThemePostCount(postData.themeId);
        } catch (error) {
          console.error('テーマ投稿数更新エラー:', error);
          // 投稿は成功したが、統計更新に失敗した場合はログのみ出力
        }
      }
      
      return docRef.id;
    } catch (error) {
      console.error('投稿作成エラー:', error);
      throw error;
    }
  }

  // Flutter版と同じ会社ID付き投稿作成
  static async createPostWithCompany(
    postData: {
      anonymousId: string;
      type: 'voice' | 'thanks' | 'theme';
      category: string;
      content: string;
      tags: string[];
      urgency: 'low' | 'medium' | 'high';
      themeId?: string;
      aiAnalysis: AIAnalysis;
      statistics: PostStatistics;
      attachments?: any[];
    },
    companyId: string
  ): Promise<string> {
    try {
      // undefinedの値を除外してFirestoreに送信
      const data: any = {
        anonymousId: postData.anonymousId,
        type: postData.type,
        category: postData.category,
        content: postData.content,
        tags: postData.tags,
        urgency: postData.urgency,
        aiAnalysis: postData.aiAnalysis,
        statistics: postData.statistics,
        companyId,
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      
      // themeIdが存在する場合のみ追加
      if (postData.themeId) {
        data.themeId = postData.themeId;
      }
      
      // attachmentsが存在する場合のみ追加
      if (postData.attachments) {
        data.attachments = postData.attachments;
      }
      
      const docRef = await addDoc(collection(db, 'posts'), data);
      
      // テーマ投稿の場合はテーマの投稿数を更新
      if (postData.themeId) {
        try {
          await ThemeService.incrementThemePostCount(postData.themeId);
        } catch (error) {
          console.error('テーマ投稿数更新エラー:', error);
          // 投稿は成功したが、統計更新に失敗した場合はログのみ出力
        }
      }
      
      return docRef.id;
    } catch (error) {
      console.error('投稿作成エラー:', error);
      throw error;
    }
  }

  // Flutter版と同じ投稿一覧取得（承認済み）- 一般ユーザー用
  static async getApprovedPosts(
    limitCount: number = 20,
    postType?: string,
    category?: string,
    companyId?: string,
  ): Promise<Post[]> {
    try {
      // Flutter版と同じクエリ構造
      let q = query(collection(db, 'posts'), where('status', '==', 'approved'));

      if (postType) {
        q = query(q, where('type', '==', postType));
      }

      if (category) {
        q = query(q, where('category', '==', category));
      }

      if (companyId) {
        q = query(q, where('companyId', '==', companyId));
      }

      q = query(q, orderBy('createdAt', 'desc'), limit(limitCount));

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        postId: doc.id,
        ...doc.data()
      } as Post));
    } catch (error) {
      console.error('投稿取得エラー:', error);
      throw error;
    }
  }

  // ページネーション対応の投稿一覧取得（承認済み）- 一般ユーザー用
  static async getApprovedPostsPaginated(
    limitCount: number = 10,
    lastDoc?: DocumentSnapshot,
    postType?: string,
    category?: string,
    companyId?: string,
  ): Promise<{ posts: Post[]; lastDoc: DocumentSnapshot | null; hasMore: boolean }> {
    try {
      let q = query(collection(db, 'posts'), where('status', '==', 'approved'));

      if (postType) {
        q = query(q, where('type', '==', postType));
      }

      if (category) {
        q = query(q, where('category', '==', category));
      }

      if (companyId) {
        q = query(q, where('companyId', '==', companyId));
      }

      if (lastDoc) {
        q = query(q, orderBy('createdAt', 'desc'), startAfter(lastDoc), limit(limitCount));
      } else {
        q = query(q, orderBy('createdAt', 'desc'), limit(limitCount));
      }

      const querySnapshot = await getDocs(q);
      const posts = querySnapshot.docs.map(doc => ({
        id: doc.id,
        postId: doc.id,
        ...doc.data()
      } as Post));

      const lastDocument = querySnapshot.docs.length > 0 
        ? querySnapshot.docs[querySnapshot.docs.length - 1] 
        : null;
      const hasMore = querySnapshot.size === limitCount;

      return {
        posts,
        lastDoc: lastDocument,
        hasMore
      };
    } catch (error) {
      console.error('投稿取得エラー:', error);
      throw error;
    }
  }

  // 承認済み投稿の総件数を取得
  static async getApprovedPostsCount(
    postType?: string,
    category?: string,
    companyId?: string,
  ): Promise<number> {
    try {
      let q = query(collection(db, 'posts'), where('status', '==', 'approved'));

      if (postType) {
        q = query(q, where('type', '==', postType));
      }

      if (category) {
        q = query(q, where('category', '==', category));
      }

      if (companyId) {
        q = query(q, where('companyId', '==', companyId));
      }

      const snapshot = await getCountFromServer(q);
      return snapshot.data().count;
    } catch (error) {
      console.error('投稿件数取得エラー:', error);
      return 0;
    }
  }

  // Flutter版と同じ投稿一覧取得（全投稿）- 管理者用
  static async getAllPostsForAdmin(
    limitCount: number = 20,
    postType?: string,
    category?: string,
    status?: 'pending' | 'approved' | 'rejected'
  ): Promise<Post[]> {
    try {
      let q = query(collection(db, 'posts'));

      if (postType) {
        q = query(q, where('type', '==', postType));
      }

      if (category) {
        q = query(q, where('category', '==', category));
      }

      if (status) {
        q = query(q, where('status', '==', status));
      }

      q = query(q, orderBy('createdAt', 'desc'), limit(limitCount));

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        postId: doc.id,
        ...doc.data()
      } as Post));
    } catch (error) {
      console.error('投稿取得エラー:', error);
      throw error;
    }
  }

  // 管理者用投稿取得（会社ID指定）
  static async getPosts(companyId: string, status?: 'pending' | 'approved' | 'rejected'): Promise<Post[]> {
    try {
      let q = query(
        collection(db, 'posts'),
        where('companyId', '==', companyId),
        orderBy('createdAt', 'desc')
      );

      if (status) {
        q = query(q, where('status', '==', status));
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        postId: doc.id,
        ...doc.data()
      } as Post));
    } catch (error) {
      console.error('投稿取得エラー:', error);
      throw error;
    }
  }

  // 会社別投稿取得（エイリアス）
  static async getPostsByCompany(companyId: string): Promise<Post[]> {
    return this.getPosts(companyId);
  }

  // Flutter版と同じ投稿取得（ID指定）- 一般ユーザー用（承認済みのみ）
  static async getPost(postId: string): Promise<Post | null> {
    try {
      const docRef = doc(db, 'posts', postId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        
        // セキュリティルールに従い、承認済み投稿のみ取得
        if (data?.status !== 'approved') {
          return null;
        }
        
        return {
          id: docSnap.id,
          postId: docSnap.id,
          ...data
        } as Post;
      }
      return null;
    } catch (error) {
      console.error('投稿取得エラー:', error);
      throw error;
    }
  }

  // Flutter版と同じ投稿取得（ID指定）- 管理者用（全投稿）
  static async getPostForAdmin(postId: string): Promise<Post | null> {
    try {
      const docRef = doc(db, 'posts', postId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          postId: docSnap.id,
          ...docSnap.data()
        } as Post;
      }
      return null;
    } catch (error) {
      console.error('投稿取得エラー:', error);
      throw error;
    }
  }

  // 投稿を更新
  static async updatePost(
    postId: string,
    updates: Partial<Post>
  ): Promise<void> {
    try {
      const docRef = doc(db, 'posts', postId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('投稿更新エラー:', error);
      throw error;
    }
  }

  // 投稿を削除
  static async deletePost(postId: string): Promise<void> {
    try {
      const docRef = doc(db, 'posts', postId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('投稿削除エラー:', error);
      throw error;
    }
  }

  // Flutter版と同じ投稿ステータス更新
  static async updatePostStatus({
    postId,
    status,
    reviewedBy,
    rejectionReason,
  }: {
    postId: string;
    status: 'pending' | 'approved' | 'rejected';
    reviewedBy?: string;
    rejectionReason?: string;
  }): Promise<void> {
    try {
      const updateData: any = {
        status,
        updatedAt: serverTimestamp(),
      };

      if (reviewedBy) {
        updateData.approvedBy = reviewedBy;
        updateData.approvedAt = serverTimestamp();
      }

      if (rejectionReason) {
        updateData.rejectionReason = rejectionReason;
      }

      const docRef = doc(db, 'posts', postId);
      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('投稿ステータス更新エラー:', error);
      throw error;
    }
  }

  // Flutter版と同じいいね追加
  static async incrementLikes(postId: string): Promise<void> {
    try {
      const docRef = doc(db, 'posts', postId);
      await updateDoc(docRef, {
        'statistics.likes': 1, // Firestoreのincrementは別途実装が必要
      });
    } catch (error) {
      console.error('いいね追加エラー:', error);
      throw error;
    }
  }

  // Flutter版と同じいいね削除
  static async decrementLikes(postId: string): Promise<void> {
    try {
      const docRef = doc(db, 'posts', postId);
      await updateDoc(docRef, {
        'statistics.likes': -1, // Firestoreのincrementは別途実装が必要
      });
    } catch (error) {
      console.error('いいね削除エラー:', error);
      throw error;
    }
  }

  // Flutter版と同じ閲覧数増加
  static async incrementViews(postId: string): Promise<void> {
    try {
      const docRef = doc(db, 'posts', postId);
      await updateDoc(docRef, {
        'statistics.views': 1, // Firestoreのincrementは別途実装が必要
      });
    } catch (error) {
      console.error('閲覧数増加エラー:', error);
      throw error;
    }
  }

  // スレッドメモを追加
  static async addThreadNote(postId: string, content: string, createdBy: string, createdByEmail?: string, createdByName?: string): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, 'threadNotes'), {
        postId,
        content,
        createdBy,
        createdByEmail,
        createdByName,
        createdAt: serverTimestamp(),
      });

      // 投稿情報を取得してタイプを確認
      const postDoc = await getDoc(doc(db, 'posts', postId));
      const postType = postDoc.exists() ? postDoc.data().type : undefined;
      

      // 監査ログを記録
      await this.recordAuditLog(postId, createdBy, 'memo_added', content, postType);

      return docRef.id;
    } catch (error) {
      console.error('スレッドメモ追加エラー:', error);
      throw error;
    }
  }

  // 投稿のスレッドメモを取得
  static async getThreadNotes(postId: string): Promise<ThreadNote[]> {
    try {
      const q = query(
        collection(db, 'threadNotes'),
        where('postId', '==', postId),
        orderBy('createdAt', 'asc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        postId: doc.data().postId,
        content: doc.data().content,
        createdBy: doc.data().createdBy,
        createdByEmail: doc.data().createdByEmail,
        createdByName: doc.data().createdByName,
        createdAt: doc.data().createdAt,
      } as ThreadNote));
    } catch (error) {
      console.error('スレッドメモ取得エラー:', error);
      throw error;
    }
  }

  // スレッドメモを削除
  static async deleteThreadNote(noteId: string): Promise<void> {
    try {
      const docRef = doc(db, 'threadNotes', noteId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('スレッドメモ削除エラー:', error);
      throw error;
    }
  }

  // 投稿のコメント件数を取得
  static async getCommentCount(postId: string): Promise<number> {
    try {
      const q = query(
        collection(db, 'threadNotes'),
        where('postId', '==', postId)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.size;
    } catch (error) {
      console.error('コメント件数取得エラー:', error);
      return 0;
    }
  }

  // 複数投稿のコメント件数を一括取得
  static async getCommentCounts(postIds: string[]): Promise<Record<string, number>> {
    try {
      const counts: Record<string, number> = {};
      
      // バッチ処理でコメント件数を取得
      const promises = postIds.map(async (postId) => {
        const count = await this.getCommentCount(postId);
        counts[postId] = count;
      });
      
      await Promise.all(promises);
      return counts;
    } catch (error) {
      console.error('コメント件数一括取得エラー:', error);
      return {};
    }
  }

  // 監査ログを記録
  private static async recordAuditLog(postId: string, userId: string, action: string, content?: string, postType?: string): Promise<void> {
    try {
      // 投稿情報を取得して会社IDを取得
      const postDoc = await getDoc(doc(db, 'posts', postId));
      if (!postDoc.exists()) {
        console.warn('投稿が見つかりません:', postId);
        return;
      }

      const postData = postDoc.data();
      const companyId = postData.companyId;
      const postContent = postData.content || '';

      if (!companyId) {
        console.warn('投稿に会社IDがありません:', postId);
        return;
      }

      // ユーザー情報を取得
      const userDoc = await getDoc(doc(db, 'users', userId));
      const userName = userDoc.exists() ? userDoc.data().displayName || userDoc.data().email || '不明' : '不明';


      // 監査ログを記録
      const auditDescription = await this.getAuditDescription(action, companyId, postType);
      await addDoc(collection(db, 'audit_logs'), {
        timestamp: serverTimestamp(),
        action: action,
        description: auditDescription,
        userId: userId,
        userName: userName,
        companyId: companyId,
        details: {
          actionType: action,
          postId: postId,
          postType: postType,
          postContent: postContent,
          postContentPreview: postContent,
          memoContent: content ? content.substring(0, 100) + (content.length > 100 ? '...' : '') : undefined,
        },
      });
      
    } catch (error) {
      console.error('監査ログ記録エラー:', error);
      // 監査ログの記録に失敗してもメモ投稿は継続
    }
  }

  // 監査ログの説明文を生成
  private static async getAuditDescription(action: string, companyId?: string, postType?: string): Promise<string> {
    let boxName = '投稿';
    if (postType && ['voice', 'thanks', 'theme'].includes(postType)) {
      try {
        const { getBoxTemplate } = await import('../constants/industryTemplates');
        let industryType: string | undefined;
        if (companyId) {
          const { doc, getDoc } = await import('firebase/firestore');
          const companyDoc = await getDoc(doc(db, 'companies', companyId));
          if (companyDoc.exists()) {
            industryType = companyDoc.data()?.industryType;
          }
        }
        boxName = getBoxTemplate(industryType, postType as 'voice' | 'thanks' | 'theme').name;
      } catch {
        // フォールバック（defaultテンプレートから取得）
        try {
          const { getBoxTemplate } = await import('../constants/industryTemplates');
          boxName = getBoxTemplate('default', postType as 'voice' | 'thanks' | 'theme').name;
        } catch {
          boxName = '投稿';
        }
      }
    }
    switch (action) {
      case 'memo_added':
        return `${boxName}にメモを書き込みました`;
      default:
        return 'アクションが実行されました';
    }
  }
}